/**
 * RBAC Configuration
 * Role-Based Access Control definitions and utilities
 */

import {
  UserRole,
  RoleDefinition,
  Permission,
  PermissionCategory,
  RBACConfig,
  ROLE_HIERARCHY,
  RoleComparison
} from '@/types/rbac';

// ===== RBAC CONFIGURATION =====
export const RBAC_CONFIG: RBACConfig = {
  enabled: true,
  defaultRole: 'customer',
  enableDebug: import.meta.env.DEV,
  enforceHierarchy: true,
  allowRoleEscalation: false,
  tokenRefreshOnLoad: true,
};

// ===== PERMISSION DEFINITIONS =====

// User management permissions
const USER_PERMISSIONS: Permission[] = [
  'users:read',
  'users:create',
  'users:update', 
  'users:delete',
  'users:manage',
  'users:invite',
  'users:export',
];

// Admin and settings permissions
const ADMIN_PERMISSIONS: Permission[] = [
  'admin:access',
  'admin:dashboard',
  'settings:read',
  'settings:update',
  'settings:manage',
  'secrets:read',
  'secrets:manage',
  'webhooks:configure',
];

// Project and content permissions
const PROJECT_PERMISSIONS: Permission[] = [
  'project:read',
  'project:update',
  'project:manage',
  'groups:read',
  'groups:create',
  'groups:update',
  'groups:delete',
  'groups:manage',
  'events:read',
  'events:create',
  'events:update',
  'events:delete',
  'events:manage',
  'channels:read',
  'channels:create',
  'channels:update',
  'channels:delete',
  'channels:manage',
  'mail:read',
  'mail:send',
  'mail:manage',
];

// Analytics and reporting permissions
const ANALYTICS_PERMISSIONS: Permission[] = [
  'analytics:read',
  'analytics:view',
  'analytics:export',
  'reports:generate',
  'data:export',
];

// Payment and commerce permissions
const PAYMENT_PERMISSIONS: Permission[] = [
  'payments:read',
  'payments:manage',
  'payments:configure',
  'marketplace:read',
  'marketplace:purchase',
  'orders:read',
  'orders:manage',
];

// Directory and profile permissions  
const DIRECTORY_PERMISSIONS: Permission[] = [
  'directory:read',
  'directory:search',
  'directory:visible',
  'profile:read',
  'profile:update',
  'profile:required_fields',
];

// ===== ROLE DEFINITIONS =====

export const ROLE_DEFINITIONS: Record<UserRole, RoleDefinition> = {
  admin: {
    id: 'admin',
    name: 'Administrator',
    description: 'Full access to all project features and administrative functions',
    hierarchy: ROLE_HIERARCHY.admin,
    permissions: [
      // Admin gets everything
      ...USER_PERMISSIONS,
      ...ADMIN_PERMISSIONS,
      ...PROJECT_PERMISSIONS,
      ...ANALYTICS_PERMISSIONS,
      ...PAYMENT_PERMISSIONS,
      ...DIRECTORY_PERMISSIONS,
    ],
    restrictions: {
      directoryVisible: true,
      profileFieldsRequired: true,
      canAccessAdmin: true,
      canInviteUsers: true,
      canViewAnalytics: true,
      canManagePayments: true,
      canExportData: true,
      canDeleteUsers: true,
      canConfigureWebhooks: true,
      canManageSecrets: true,
    },
  },

  owner: {
    id: 'owner',
    name: 'Project Owner',
    description: 'Administrative access with some restrictions on critical operations',
    hierarchy: ROLE_HIERARCHY.owner,
    permissions: [
      // Users (but not delete)
      'users:read',
      'users:create',
      'users:update',
      'users:manage',
      'users:invite',
      'users:export',
      
      // Admin access (but not secrets/webhooks)
      'admin:access',
      'admin:dashboard',
      'settings:read',
      'settings:update',
      'settings:manage',
      
      // Full project permissions
      ...PROJECT_PERMISSIONS,
      
      // Analytics
      ...ANALYTICS_PERMISSIONS,
      
      // Limited payment access (no configuration)
      'payments:read',
      'marketplace:read',
      'marketplace:purchase',
      'orders:read',
      'orders:manage',
      
      // Directory access
      ...DIRECTORY_PERMISSIONS,
    ],
    restrictions: {
      directoryVisible: true,
      profileFieldsRequired: true,
      canAccessAdmin: true,
      canInviteUsers: true,
      canViewAnalytics: true,
      canManagePayments: false, // Cannot manage payment configuration
      canExportData: true,
      canDeleteUsers: false, // Cannot delete users
      canConfigureWebhooks: false, // Cannot configure webhooks
      canManageSecrets: false, // Cannot access secrets
    },
  },

  customer: {
    id: 'customer', 
    name: 'Customer',
    description: 'Standard user access to main features',
    hierarchy: ROLE_HIERARCHY.customer,
    permissions: [
      // Basic read access to users
      'users:read',
      
      // No admin access
      
      // Limited project access (read only for most)
      'project:read',
      'groups:read',
      'events:read',
      'channels:read',
      
      // No analytics access
      
      // Marketplace access
      'marketplace:read',
      'marketplace:purchase',
      'orders:read',
      
      // Directory access
      'directory:read',
      'directory:search',
      'directory:visible',
      'profile:read',
      'profile:update',
      'profile:required_fields',
    ],
    restrictions: {
      directoryVisible: true,
      profileFieldsRequired: true,
      canAccessAdmin: false,
      canInviteUsers: false,
      canViewAnalytics: false,
      canManagePayments: false,
      canExportData: false,
      canDeleteUsers: false,
      canConfigureWebhooks: false,
      canManageSecrets: false,
    },
  },

  viewer: {
    id: 'viewer',
    name: 'Viewer',
    description: 'Most restricted access - viewing only',
    hierarchy: ROLE_HIERARCHY.viewer,
    permissions: [
      // Very limited read access
      'users:read',
      'project:read',
      'groups:read',
      'events:read',
      'channels:read',
      
      // Limited marketplace access (view only)
      'marketplace:read',
      
      // Profile access but fields not required
      'profile:read',
      'profile:update',
    ],
    restrictions: {
      directoryVisible: false, // Key restriction - not visible in directory
      profileFieldsRequired: false, // Profile fields are optional
      canAccessAdmin: false,
      canInviteUsers: false,
      canViewAnalytics: false,
      canManagePayments: false,
      canExportData: false,
      canDeleteUsers: false,
      canConfigureWebhooks: false,
      canManageSecrets: false,
    },
  },
};

// ===== PERMISSION CATEGORIES =====

export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: 'users',
    name: 'User Management',
    description: 'Permissions related to managing users and profiles',
    permissions: USER_PERMISSIONS,
  },
  {
    id: 'admin',
    name: 'Administration',
    description: 'Administrative and configuration permissions',
    permissions: ADMIN_PERMISSIONS,
  },
  {
    id: 'project',
    name: 'Project Management',
    description: 'Permissions for managing project content and features',
    permissions: PROJECT_PERMISSIONS,
  },
  {
    id: 'analytics',
    name: 'Analytics & Reporting',
    description: 'Permissions for viewing analytics and generating reports',
    permissions: ANALYTICS_PERMISSIONS,
  },
  {
    id: 'payments',
    name: 'Payments & Commerce',
    description: 'Permissions related to payments and marketplace',
    permissions: PAYMENT_PERMISSIONS,
  },
  {
    id: 'directory',
    name: 'Directory & Profiles',
    description: 'Permissions for directory access and profile management',
    permissions: DIRECTORY_PERMISSIONS,
  },
];

// ===== RBAC HELPER FUNCTIONS =====

export const RBAC_HELPERS = {
  /**
   * Get role display name
   */
  getRoleDisplayName(role: UserRole): string {
    return ROLE_DEFINITIONS[role]?.name || role;
  },

  /**
   * Get role description
   */
  getRoleDescription(role: UserRole): string {
    return ROLE_DEFINITIONS[role]?.description || '';
  },

  /**
   * Check if a role has a specific permission
   */
  hasPermission(role: UserRole, permission: Permission): boolean {
    const roleDefinition = ROLE_DEFINITIONS[role];
    if (!roleDefinition) return false;
    
    return roleDefinition.permissions.includes(permission);
  },

  /**
   * Check if a role meets or exceeds another role (hierarchy check)
   */
  hasRole(userRole: UserRole, requiredRole: UserRole): boolean {
    const userHierarchy = ROLE_HIERARCHY[userRole];
    const requiredHierarchy = ROLE_HIERARCHY[requiredRole];
    
    if (!userHierarchy || !requiredHierarchy) return false;
    
    // Lower number = higher priority
    return userHierarchy <= requiredHierarchy;
  },

  /**
   * Compare two roles
   */
  compareRoles(role1: UserRole, role2: UserRole): RoleComparison {
    const hierarchy1 = ROLE_HIERARCHY[role1];
    const hierarchy2 = ROLE_HIERARCHY[role2];
    
    if (!hierarchy1 || !hierarchy2) return 'equal';
    
    if (hierarchy1 < hierarchy2) return 'higher';
    if (hierarchy1 > hierarchy2) return 'lower';
    return 'equal';
  },

  /**
   * Get all permissions for a role
   */
  getRolePermissions(role: UserRole): Permission[] {
    return ROLE_DEFINITIONS[role]?.permissions || [];
  },

  /**
   * Get role restrictions
   */
  getRoleRestrictions(role: UserRole) {
    return ROLE_DEFINITIONS[role]?.restrictions || {};
  },

  /**
   * Check if role can access admin areas
   */
  canAccessAdmin(role: UserRole): boolean {
    return ROLE_DEFINITIONS[role]?.restrictions.canAccessAdmin || false;
  },

  /**
   * Check if role is visible in directory
   */
  isDirectoryVisible(role: UserRole): boolean {
    return ROLE_DEFINITIONS[role]?.restrictions.directoryVisible || false;
  },

  /**
   * Check if profile fields are required for this role
   */
  areProfileFieldsRequired(role: UserRole): boolean {
    return ROLE_DEFINITIONS[role]?.restrictions.profileFieldsRequired || false;
  },

  /**
   * Get all roles that have a specific permission
   */
  getRolesWithPermission(permission: Permission): UserRole[] {
    return Object.keys(ROLE_DEFINITIONS).filter(role => 
      this.hasPermission(role as UserRole, permission)
    ) as UserRole[];
  },

  /**
   * Get all roles at or above a hierarchy level
   */
  getRolesAtOrAbove(role: UserRole): UserRole[] {
    const targetHierarchy = ROLE_HIERARCHY[role];
    if (!targetHierarchy) return [];
    
    return Object.entries(ROLE_HIERARCHY)
      .filter(([_, hierarchy]) => hierarchy <= targetHierarchy)
      .map(([role, _]) => role as UserRole);
  },

  /**
   * Filter users for directory based on role visibility
   */
  filterUsersForDirectory(users: any[]): any[] {
    return users.filter(user => {
      // If user has no role info, default to visible (backward compatibility)
      if (!user.role && !user.projectRole) return true;
      
      const userRole = user.role || user.projectRole;
      return this.isDirectoryVisible(userRole);
    });
  },

  /**
   * Get appropriate fallback route for a role
   */
  getFallbackRoute(role: UserRole, projectId?: string): string {
    const basePrefix = projectId ? `/${projectId}` : '';
    
    switch (role) {
      case 'admin':
      case 'owner':
        return `${basePrefix}/admin`;
      case 'customer':
        return `${basePrefix}/directory`;
      case 'viewer':
        return `${basePrefix}/marketplace`;
      default:
        return `${basePrefix}/home`;
    }
  },

  /**
   * Get forbidden/access denied route
   */
  getForbiddenRoute(projectId?: string): string {
    // For white label projects, forbidden page should be within project context
    if (projectId) {
      return `/${projectId}/forbidden`;
    }
    // For private label or no project context, use global forbidden page
    return '/forbidden';
  },
};

// Export everything
export {
  USER_PERMISSIONS,
  ADMIN_PERMISSIONS,
  PROJECT_PERMISSIONS,
  ANALYTICS_PERMISSIONS,
  PAYMENT_PERMISSIONS,
  DIRECTORY_PERMISSIONS,
}; 